/**
 * Renders an issue into email_campaigns.body_html.
 *
 * body_html is shared by every recipient, so per-recipient links cannot be
 * baked in here. This emits placeholders that the send worker substitutes per
 * recipient (Task 9/10), in the same place renderMergeFields already runs.
 *
 * The signature block and COMPLIANCE_FOOTER_HTML are appended later by
 * composeHtml() in server/services/blast.ts. Do not add them here or they
 * render twice.
 *
 * Placeholder indexing: interestPlaceholder(i) is keyed by POSITION IN THE
 * SORTED CARD LIST (sorted by sortOrder), not input array order. The send
 * worker re-sorts by sortOrder the same way and maps index -> card, so
 * emitting placeholders in input order would point every buyer's "Request
 * teaser" link at the wrong deal.
 */
import type { NewsletterCard } from "./newsletterTypes";
import { escapeHtml } from "./htmlEscape";

export const UNSUB_TOKEN = "__UNSUB_TOKEN__";
export const interestPlaceholder = (i: number): string => `__INTEREST_TOKEN_${i}__`;

const MUTED = "#6b7280";
const INK = "#111827";
const RULE = "#e5e7eb";

function renderCard(c: NewsletterCard, i: number, baseUrl: string): string {
  const moneyRows = [
    c.revenue
      ? `<tr><td style="padding:2px 16px 2px 0;color:${MUTED};font-size:13px">Revenue</td>` +
        `<td style="padding:2px 0;color:${INK};font-size:13px;font-weight:600">${escapeHtml(c.revenue)}</td></tr>`
      : "",
    c.ebitda
      ? `<tr><td style="padding:2px 16px 2px 0;color:${MUTED};font-size:13px">EBITDA</td>` +
        `<td style="padding:2px 0;color:${INK};font-size:13px;font-weight:600">${escapeHtml(c.ebitda)}</td></tr>`
      : "",
  ].join("");

  const bullets = c.bullets.length
    ? `<ul style="margin:8px 0 0;padding-left:18px;color:${INK};font-size:13px">` +
      c.bullets.map((b) => `<li style="margin:2px 0">${escapeHtml(b)}</li>`).join("") +
      `</ul>`
    : "";

  return `
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="border:1px solid ${RULE};border-radius:6px;margin:0 0 16px">
  <tr><td style="padding:16px">
    ${c.projectName ? `<div style="font-size:16px;font-weight:700;color:${INK};margin:0 0 2px">${escapeHtml(c.projectName)}</div>` : ""}
    <div style="font-size:12px;letter-spacing:.06em;font-weight:700;color:${MUTED};text-transform:uppercase">${escapeHtml(c.headline)}</div>
    ${moneyRows ? `<table role="presentation" cellpadding="0" cellspacing="0" style="margin:10px 0 0">${moneyRows}</table>` : ""}
    <p style="margin:10px 0 0;color:${INK};font-size:14px;line-height:1.5">${escapeHtml(c.blurb)}</p>
    ${bullets}
    <p style="margin:14px 0 0">
      <a href="${baseUrl}/api/newsletter/interest?token=${interestPlaceholder(i)}"
         style="font-size:13px;font-weight:600;color:#1d4ed8;text-decoration:none">Request teaser</a>
    </p>
  </td></tr>
</table>`;
}

export function renderNewsletter(args: {
  intro: string;
  cards: NewsletterCard[];
  baseUrl: string;
}): string {
  const { intro, cards, baseUrl } = args;

  // Inline styles, matching every other element in this template: mail
  // clients strip <style> blocks, so inline styles are the only styling that
  // survives delivery. A bare <p> here would fall back to the client's
  // default face (e.g. Outlook's Times New Roman) directly above fully
  // styled deal cards.
  const introHtml = intro
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) => `<p style="margin:0 0 12px;color:${INK};font-size:14px;line-height:1.6">${escapeHtml(p)}</p>`)
    .join("");

  // Index is position in the SORTED list, which is what the send worker walks.
  const sorted = [...cards].sort((a, b) => a.sortOrder - b.sortOrder);
  const cardsHtml = sorted.map((c, i) => renderCard(c, i, baseUrl)).join("");

  return `
<div style="max-width:600px;font-family:-apple-system,Segoe UI,Helvetica,Arial,sans-serif;color:${INK};font-size:14px;line-height:1.6">
  <div style="font-size:12px;letter-spacing:.08em;text-transform:uppercase;color:${MUTED};padding-bottom:8px;border-bottom:2px solid ${INK};margin-bottom:16px">Current Opportunities</div>
  ${introHtml}
  ${cardsHtml}
  <p style="margin:24px 0 0;font-size:11px;color:${MUTED};line-height:1.5">
    You are receiving this because you are part of our buyer network.
    <a href="${baseUrl}/api/newsletter/unsubscribe?token=${UNSUB_TOKEN}" style="color:${MUTED}">Unsubscribe</a>.
  </p>
</div>`.trim();
}
